<script setup lang="ts">
import { computed, ref } from 'vue'
import { BriefcaseBusiness, ChevronDown, ChevronUp, Copy, Expand, Minimize2, PencilLine, RefreshCw, Sparkles, X } from 'lucide-vue-next'

import AppButton from '@/components/atoms/buttons/AppButton.vue'
import AppSelect from '@/components/atoms/selects/AppSelect.vue'
import AppTextarea from '@/components/atoms/textarea/AppTextarea.vue'
import { useI18n } from 'vue-i18n'

type AssistantAction = 'improve' | 'formalize' | 'develop' | 'reduce' | 'generate' | 'custom'

interface ActionOption {
  name: string
  value: AssistantAction
}

interface Props {
  isOpen?: boolean
  loading?: boolean
  result?: string
  modelValue?: string
  sourceText?: string
  selectedAction?: AssistantAction
  instruction?: string
  targetLanguage?: string
  actions?: AssistantAction[]
  canAppend?: boolean
  panelClass?: string
  showComposer?: boolean
  compactComposer?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  loading: false,
  result: '',
  modelValue: '',
  sourceText: '',
  selectedAction: 'generate',
  instruction: '',
  targetLanguage: 'en',
  actions: () => [],
  canAppend: true,
  panelClass: '',
  showComposer: false,
  compactComposer: false,
})
const { t } = useI18n()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update:instruction', value: string): void
  (e: 'update:targetLanguage', value: string): void
  (e: 'select-action', value: AssistantAction): void
  (e: 'execute'): void
  (e: 'regenerate'): void
  (e: 'replace'): void
  (e: 'append'): void
  (e: 'copy'): void
}>()

const ACTION_META = computed<Record<AssistantAction, { label: string; icon: object; needsInstruction: boolean }>>(() => ({
  improve: { label: t('ai.assist_popover.improve'), icon: Sparkles, needsInstruction: false },
  formalize: { label: t('ai.assist_popover.formalize'), icon: BriefcaseBusiness, needsInstruction: false },
  develop: { label: t('ai.assist_popover.develop'), icon: Expand, needsInstruction: false },
  reduce: { label: t('ai.assist_popover.reduce'), icon: Minimize2, needsInstruction: false },
  generate: { label: t('ai.assist_popover.help_me_write'), icon: Sparkles, needsInstruction: true },
  custom: { label: t('ai.assist_popover.customize'), icon: PencilLine, needsInstruction: true },
}))

const languageOptions = computed(() => [
  { name: t('ai.languages.english'), value: 'en' },
  { name: t('ai.languages.spanish'), value: 'es' },
  { name: t('ai.languages.portuguese'), value: 'pt' },
  { name: t('ai.languages.french'), value: 'fr' },
])

const MAX_INSTRUCTION_CHARS = 4000

const polishMenuOpen = ref(false)
const moreMenuOpen = ref(false)

const currentMeta = computed(() => ACTION_META.value[props.selectedAction])
const needsInstruction = computed(() => currentMeta.value.needsInstruction)
const sourceText = computed(() => String(props.sourceText || props.modelValue || '').trim())
const hasInstruction = computed(() => String(props.instruction || '').trim().length > 0)
const hasResult = computed(() => String(props.result || '').trim().length > 0)
const canExecute = computed(() => {
  if (props.loading) return false
  if (!needsInstruction.value) return true
  return hasInstruction.value
})
const actionOptions = computed<ActionOption[]>(() =>
  props.actions.map((action) => ({
    name: ACTION_META.value[action]?.label || action,
    value: action,
  })),
)
const hasTargetText = computed(() => String(props.modelValue || '').trim().length > 0)
const moreOptions = computed(() => {
  const options = []
  if (props.canAppend && hasTargetText.value) {
    options.push({ name: t('ai.assist_popover.insert_below'), value: 'append' })
  }
  options.push({ name: t('ai.assist_popover.copy'), value: 'copy' })
  return options
})
const actionButtonLabel = computed(() =>
  props.selectedAction === 'custom' ? t('ai.assist_popover.customize') : t('ai.buttons.generate'),
)

function updateInstruction(value: string) {
  emit('update:instruction', value.slice(0, MAX_INSTRUCTION_CHARS))
}

function updateTargetLanguage(value: string | number | boolean | null) {
  emit('update:targetLanguage', String(value ?? 'en'))
}

function handleMoreAction(value: string) {
  moreMenuOpen.value = false
  if (value === 'append') emit('append')
  if (value === 'copy') emit('copy')
}

function handleReplaceAction() {
  moreMenuOpen.value = false
  emit('replace')
}

function handleSelectActionOption(action: AssistantAction) {
  polishMenuOpen.value = false
  moreMenuOpen.value = false
  emit('select-action', action)
}

function handleComposerEnter() {
  if (!canExecute.value) return
  emit('execute')
}
</script>

<template>
  <div
    v-if="isOpen"
    :class="[
      panelClass,
      compactComposer ? 'w-[34rem]' : 'w-[26rem]',
      compactComposer
        ? ''
        : 'rounded-[1.5rem] border border-[var(--p-surface-200)] bg-white p-4 shadow-2xl dark:border-[var(--p-surface-700)] dark:bg-[var(--p-surface-900)]'
    ]"
  >
    <div v-if="!compactComposer" class="mb-3 flex items-center justify-between gap-3">
      <div class="flex items-center gap-2 text-sm font-semibold text-[var(--p-surface-800)] dark:text-[var(--p-surface-100)]">
        <Sparkles class="h-4 w-4 text-[var(--p-primary-color)]" />
        <span>{{ t('ai.assistant') }}</span>
      </div>

      <button
        type="button"
        class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[var(--p-surface-500)] transition hover:bg-[var(--p-surface-100)] hover:text-[var(--p-surface-700)] dark:hover:bg-[var(--p-surface-800)] dark:hover:text-[var(--p-surface-100)]"
        @click="$emit('close')"
      >
        <X class="h-4 w-4" />
      </button>
    </div>

    <div v-if="compactComposer" class="space-y-3">
      <div
        v-if="showComposer"
        class="rounded-[1.35rem] border border-[var(--p-surface-200)] bg-[linear-gradient(90deg,#ffffff_0%,color-mix(in_srgb,var(--p-primary-color)_18%,white)_100%)] px-3 py-2.5 shadow-[0_10px_30px_rgba(15,23,42,0.06)] dark:border-[var(--p-surface-700)] dark:bg-[linear-gradient(135deg,color-mix(in_srgb,var(--p-primary-color)_10%,var(--p-surface-900))_0%,var(--p-surface-900)_100%)]"
      >
        <div
          v-if="selectedAction === 'custom' && sourceText"
          class="mb-3 max-h-24 overflow-y-auto rounded-xl bg-white/70 px-3 py-2 text-xs leading-5 text-[var(--p-surface-700)] dark:bg-black/20 dark:text-[var(--p-surface-100)]"
        >
          {{ sourceText }}
        </div>

        <div class="flex items-center gap-3">
          <Sparkles class="h-4 w-4 shrink-0 text-[var(--p-primary-color)]" />

          <input
            :value="instruction"
            type="text"
            :maxlength="MAX_INSTRUCTION_CHARS"
            class="min-w-0 flex-1 bg-transparent text-xs text-[var(--p-surface-700)] outline-none placeholder:text-[var(--p-surface-400)] dark:text-[var(--p-surface-100)] dark:placeholder:text-[var(--p-surface-400)]"
            :placeholder="
              selectedAction === 'custom'
                ? t('ai.assist_popover.customize_placeholder')
                : t('ai.assist_popover.generate_placeholder')
            "
            @input="updateInstruction(($event.target as HTMLInputElement).value)"
            @keydown.enter.prevent="handleComposerEnter"
          />

          <button
            type="button"
            class="shrink-0 whitespace-nowrap text-xs font-medium text-[var(--p-surface-500)] transition hover:text-[var(--p-surface-700)] dark:text-[var(--p-surface-300)] dark:hover:text-[var(--p-surface-100)]"
            @click="$emit('close')"
          >
            {{ t('general.cancel') }}
          </button>

          <AppButton
            type="button"
            :label="actionButtonLabel"
            variant="primary"
            :block="false"
            class="!w-auto !rounded-full !px-4 !py-2 !min-w-[120px]"
            :disabled="!canExecute"
            :loading="loading"
            @click="$emit('execute')"
          />
        </div>
      </div>

      <div
        v-if="loading || hasResult"
        class="space-y-3 rounded-[1.35rem] border border-[var(--p-surface-200)] bg-[linear-gradient(135deg,#ffffff_0%,color-mix(in_srgb,var(--p-primary-color)_14%,white)_100%)] p-3 shadow-[0_10px_30px_rgba(15,23,42,0.06)] dark:border-[var(--p-surface-700)] dark:bg-[linear-gradient(135deg,color-mix(in_srgb,var(--p-primary-color)_12%,var(--p-surface-900))_0%,color-mix(in_srgb,var(--p-primary-color)_4%,var(--p-surface-900))_100%)]"
      >
        <div class="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-[1.2rem] border border-white/70 bg-white/70 px-4 py-4 text-xs leading-6 text-[var(--p-surface-800)] dark:border-[var(--p-surface-600)] dark:bg-[color-mix(in_srgb,var(--p-primary-color)_6%,var(--p-surface-950))] dark:text-[var(--p-surface-50)]">
          <span v-if="loading">{{ t('ai.assist_popover.processing') }}</span>
          <span v-else>{{ result }}</span>
        </div>

        <div
          class="rounded-[1.35rem] border border-[var(--p-surface-200)] bg-[linear-gradient(135deg,#ffffff_0%,color-mix(in_srgb,var(--p-primary-color)_12%,white)_100%)] px-3 py-2.5 shadow-[0_10px_30px_rgba(15,23,42,0.06)] dark:border-[var(--p-surface-700)] dark:bg-[linear-gradient(135deg,color-mix(in_srgb,var(--p-primary-color)_10%,var(--p-surface-900))_0%,color-mix(in_srgb,var(--p-primary-color)_3%,var(--p-surface-900))_100%)]"
        >
          <div class="flex flex-wrap items-center gap-2">
            <AppButton
              type="button"
              :label="t('ai.assist_popover.retry')"
              variant="secondary"
              text
              :block="false"
              class="!w-auto !rounded-full px-4 py-2"
              :disabled="loading"
              @click="$emit('regenerate')"
            >
              <template #icon-start>
                <RefreshCw :size="14" />
              </template>
            </AppButton>

            <div class="relative">
              <AppButton
                type="button"
                :label="t('ai.assist_popover.polish')"
                variant="secondary"
                text
                :block="false"
                class="!w-auto !rounded-full px-4 py-2"
                :disabled="loading"
                @click="polishMenuOpen = !polishMenuOpen"
              >
                <template #icon-start>
                  <Sparkles :size="14" />
                </template>
                <template #icon-end>
                  <ChevronUp v-if="!polishMenuOpen" :size="14" />
                  <ChevronDown v-else :size="14" />
                </template>
              </AppButton>

              <div
                v-if="polishMenuOpen"
                class="absolute bottom-[calc(100%+0.5rem)] left-0 z-10 min-w-60 rounded-xl border border-[var(--p-surface-200)] bg-white py-2 shadow-xl dark:border-[var(--p-surface-700)] dark:bg-[var(--p-surface-900)]"
              >
                <button
                  v-for="option in actionOptions"
                  :key="option.value"
                  type="button"
                  class="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-[var(--p-surface-700)] transition hover:bg-[var(--p-surface-100)] dark:text-[var(--p-surface-100)] dark:hover:bg-[var(--p-surface-800)]"
                  @click="handleSelectActionOption(option.value)"
                >
                  <component :is="ACTION_META[option.value].icon" class="h-4 w-4 shrink-0" />
                  <span>{{ option.name }}</span>
                </button>
              </div>
            </div>

            <div class="relative ml-auto flex items-center rounded-full bg-[var(--p-primary-color)] text-white">
              <button
                type="button"
                class="inline-flex h-9 min-w-[7rem] items-center justify-center rounded-l-full px-4 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="!hasResult || loading"
                @click="$emit('replace')"
              >
                {{ hasTargetText ? t('ai.assist_popover.replace') : t('ai.buttons.insert') }}
              </button>

              <button
                type="button"
                class="inline-flex h-9 w-9 items-center justify-center rounded-r-full border-l border-white/30 transition disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="!hasResult || loading"
                @click="moreMenuOpen = !moreMenuOpen"
              >
                <ChevronUp v-if="!moreMenuOpen" :size="14" />
                <ChevronDown v-else :size="14" />
              </button>

              <div
                v-if="moreMenuOpen"
                class="absolute bottom-[calc(100%+0.5rem)] right-0 z-10 min-w-52 rounded-xl border border-[var(--p-surface-200)] bg-white py-2 shadow-xl dark:border-[var(--p-surface-700)] dark:bg-[var(--p-surface-900)]"
              >
                <button
                class="flex w-full items-center px-4 py-2 text-left text-sm text-[var(--p-surface-700)] transition hover:bg-[var(--p-surface-100)] dark:text-[var(--p-surface-100)] dark:hover:bg-[var(--p-surface-800)]"
                  @click="handleReplaceAction"
                >
                  {{ t('ai.assist_popover.replace') }}
                </button>
                <button
                  v-for="option in moreOptions"
                  :key="option.value"
                  type="button"
                  class="flex w-full items-center px-4 py-2 text-left text-sm text-[var(--p-surface-700)] transition hover:bg-[var(--p-surface-100)] dark:text-[var(--p-surface-100)] dark:hover:bg-[var(--p-surface-800)]"
                  @click="handleMoreAction(option.value)"
                >
                  {{ option.name }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <div v-if="showComposer" class="space-y-3">
        <div
          v-if="selectedAction === 'custom' && sourceText"
          class="max-h-24 overflow-y-auto rounded-xl border border-[var(--p-surface-200)] bg-[var(--p-surface-50)] px-3 py-2 text-xs leading-5 text-[var(--p-surface-700)] dark:border-[var(--p-surface-700)] dark:bg-[color-mix(in_srgb,var(--p-primary-color)_8%,var(--p-surface-900))] dark:text-[var(--p-surface-100)]"
        >
          {{ sourceText }}
        </div>

        <AppTextarea
          :model-value="instruction"
          :rows="4"
          :use-float-label="false"
          :maxlength="MAX_INSTRUCTION_CHARS"
          :placeholder="
            selectedAction === 'custom'
              ? t('ai.assist_popover.customize_placeholder')
              : t('ai.assist_popover.generate_placeholder')
          "
          class="!w-full !rounded-2xl border border-[var(--p-surface-200)] bg-[var(--p-surface-50)] dark:!border-[var(--p-surface-600)] dark:!bg-[color-mix(in_srgb,var(--p-primary-color)_8%,var(--p-surface-900))] dark:!text-[var(--p-surface-50)]"
          @update:model-value="updateInstruction"
          @keydown.enter.exact.prevent="handleComposerEnter"
        />
      </div>

      <div
        v-else
        class="rounded-2xl border border-[var(--p-surface-200)] bg-[var(--p-surface-50)] px-4 py-4 text-xs leading-6 text-[var(--p-surface-800)] dark:border-[var(--p-surface-700)] dark:bg-[color-mix(in_srgb,var(--p-primary-color)_6%,var(--p-surface-900))] dark:text-[var(--p-surface-50)]"
      >
        <span v-if="loading">{{ t('ai.assist_popover.processing') }}</span>
        <span v-else-if="hasResult">{{ result }}</span>
        <span v-else>{{ t('ai.assist_popover.select_action_hint') }}</span>
      </div>

      <div v-if="selectedAction === 'generate'" class="mt-3">
        <label class="mb-1 block text-xs font-medium text-[var(--p-surface-700)] dark:text-[var(--p-surface-200)]">
          {{ t('ai.labels.target_language') }}
        </label>
        <AppSelect
          :model-value="targetLanguage"
          :options="languageOptions"
          :show-clear="false"
          :editable="false"
          :filter="false"
          @update:model-value="updateTargetLanguage"
        />
      </div>

      <div class="mt-4 flex flex-wrap items-center gap-2">
        <AppButton
          type="button"
          :label="showComposer ? t('ai.buttons.generate') : t('ai.assist_popover.retry')"
          variant="secondary"
          text
          :block="false"
          class="!w-auto !rounded-full px-4 py-2"
          :disabled="showComposer ? !canExecute : loading"
          @click="showComposer ? $emit('execute') : $emit('regenerate')"
        >
          <template #icon-start>
            <RefreshCw :size="14" />
          </template>
        </AppButton>

        <div class="relative">
          <AppButton
            type="button"
            :label="t('ai.assist_popover.polish')"
            variant="secondary"
            text
            :block="false"
            class="!w-auto !rounded-full px-4 py-2"
            @click="polishMenuOpen = !polishMenuOpen"
          >
            <template #icon-start>
              <Sparkles :size="14" />
            </template>
          </AppButton>

          <div
            v-if="polishMenuOpen"
            class="absolute bottom-[calc(100%+0.5rem)] left-0 z-10 min-w-60 rounded-xl border border-[var(--p-surface-200)] bg-white py-2 shadow-xl dark:border-[var(--p-surface-700)] dark:bg-[var(--p-surface-900)]"
          >
            <button
              v-for="option in actionOptions"
              :key="option.value"
              type="button"
              class="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-[var(--p-surface-700)] transition hover:bg-[var(--p-surface-100)] dark:text-[var(--p-surface-100)] dark:hover:bg-[var(--p-surface-800)]"
              @click="handleSelectActionOption(option.value)"
            >
              <component :is="ACTION_META[option.value].icon" class="h-4 w-4 shrink-0" />
              <span>{{ option.name }}</span>
            </button>
          </div>
        </div>

        <AppButton
          type="button"
          :label="hasTargetText ? t('ai.assist_popover.replace') : t('ai.buttons.insert')"
          variant="primary"
          :block="false"
          class="!w-auto !rounded-full px-4 py-2"
          :disabled="!hasResult || loading"
          @click="$emit('replace')"
        />

        <div class="relative">
          <AppButton
            type="button"
            :label="t('ai.assist_popover.more')"
            variant="secondary"
            text
            :block="false"
            class="!w-auto !rounded-full px-4 py-2"
            :disabled="!hasResult || loading"
            @click="moreMenuOpen = !moreMenuOpen"
          >
            <template #icon-start>
              <Copy :size="14" />
            </template>
          </AppButton>

          <div
            v-if="moreMenuOpen"
            class="absolute bottom-[calc(100%+0.5rem)] right-0 z-10 min-w-52 rounded-xl border border-[var(--p-surface-200)] bg-white py-2 shadow-xl dark:border-[var(--p-surface-700)] dark:bg-[var(--p-surface-900)]"
          >
            <button
              v-for="option in moreOptions"
              :key="option.value"
              type="button"
              class="flex w-full items-center px-4 py-2 text-left text-sm text-[var(--p-surface-700)] transition hover:bg-[var(--p-surface-100)] dark:text-[var(--p-surface-100)] dark:hover:bg-[var(--p-surface-800)]"
              @click="handleMoreAction(option.value)"
            >
              {{ option.name }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
